/**
 * Exit is still "in flight" for the employee — block submitting another resignation.
 * Terminal / cleared: REJECTED, WITHDRAWN, COMPLETED (and unknowns are not assumed blocking).
 */
const BLOCKS_NEW_SUBMISSION = new Set([
  "DRAFT",
  "SUBMITTED",
  "PENDING_APPROVAL",
  "APPROVED",
  /** V2 API: in approval chain */
  "MANAGER_APPROVAL",
  "HR_REVIEW",
  "NOTICE_PERIOD",
  "EXIT_IN_PROGRESS",
  "FINAL_SETTLEMENT",
]);

/**
 * Immediate withdrawal (exit becomes WITHDRAWN right away).
 * Backend V2 supports this for early phases; keep legacy aliases for compatibility.
 */
const FREE_WITHDRAW_PHASES = new Set([
  "SUBMITTED",
  "MANAGER_APPROVAL",
  "HR_REVIEW",
  /** Legacy aliases still seen on older payloads */
  "DRAFT",
  "PENDING_APPROVAL",
]);

/** Pullback request flow (approval required after submit). */
const HR_GATED_WITHDRAW_PHASES = new Set([
  "NOTICE_PERIOD",
  "EXIT_IN_PROGRESS",
  "FINAL_SETTLEMENT",
  /** Legacy alias */
  "APPROVED",
]);

function normalizeExitStatus(status: string | undefined): string {
  return String(status ?? "").toUpperCase();
}

export function exitStatusBlocksNewSubmission(
  status: string | undefined,
): boolean {
  if (!status) return false;
  return BLOCKS_NEW_SUBMISSION.has(normalizeExitStatus(status));
}

/** Prefer `exitStatus` from v2 my-exit payload, then legacy `status`. */
export function getExitLifecyclePhase(exit: {
  exitStatus?: string;
  status?: string;
}): string | undefined {
  const raw = exit.exitStatus ?? exit.status;
  return raw !== undefined && raw !== null && String(raw).length > 0
    ? String(raw)
    : undefined;
}

export function exitRecordBlocksNewSubmission(exit: {
  exitStatus?: string;
  status?: string;
}): boolean {
  return exitStatusBlocksNewSubmission(getExitLifecyclePhase(exit));
}

export type ExitWithdrawEligibility =
  | { kind: "none"; reason?: string }
  | { kind: "free" }
  | { kind: "hr_gated" };

/**
 * Employee withdrawal rules (aligned with POST /v2/exit/:id/withdraw):
 * - Free: early workflow states → immediate withdrawal.
 * - HR-gated: notice/final phases → pullback request goes for approval.
 * - None: Exit completed, or already withdrawn/rejected, or unknown inactive phase.
 */
export function getExitWithdrawEligibility(
  phase: string | undefined,
): ExitWithdrawEligibility {
  const s = normalizeExitStatus(phase);
  if (!s) return { kind: "none" };
  if (s === "COMPLETED" || s === "EXIT_COMPLETED") {
    return {
      kind: "none",
      reason: "Withdrawal is not allowed after exit is completed.",
    };
  }
  if (
    s === "WITHDRAWN" ||
    s === "REJECTED" ||
    s === "LINKED_TERMINATION_CANCELLED"
  ) {
    return { kind: "none" };
  }
  if (FREE_WITHDRAW_PHASES.has(s)) return { kind: "free" };
  if (HR_GATED_WITHDRAW_PHASES.has(s)) return { kind: "hr_gated" };
  if (exitStatusBlocksNewSubmission(s)) return { kind: "hr_gated" };
  return { kind: "none" };
}

/** Employer-initiated exit (linked to a termination workflow). */
export function isEmployerInitiatedExit(exit: {
  exitInitiationSource?: string;
}): boolean {
  return String(exit.exitInitiationSource ?? "").toUpperCase() === "TERMINATION";
}

export function exitRecordWithdrawEligibility(exit: {
  exitStatus?: string;
  status?: string;
  exitInitiationSource?: string;
  pullbackRequestStatus?: string;
}): ExitWithdrawEligibility {
  if (String(exit.pullbackRequestStatus ?? "").toUpperCase() === "REQUESTED") {
    return {
      kind: "none",
      reason: "Pullback requested",
    };
  }
  if (isEmployerInitiatedExit(exit)) {
    return {
      kind: "none",
      reason:
        "Employer-initiated terminations cannot be withdrawn here. Contact HR if you have questions.",
    };
  }
  return getExitWithdrawEligibility(getExitLifecyclePhase(exit));
}

/** Prefer an in-flight exit; otherwise the most recently updated row (history list from GET my-exit). */
export function selectPrimaryMyExit<
  T extends { exitStatus?: string; status?: string; updatedAt?: string; createdAt?: string },
>(exits: T[]): T | null {
  if (!exits.length) return null;
  const active = exits.find((e) => exitRecordBlocksNewSubmission(e));
  if (active) return active;
  return [...exits].sort((a, b) => {
    const ta = Date.parse(a.updatedAt ?? a.createdAt ?? "") || 0;
    const tb = Date.parse(b.updatedAt ?? b.createdAt ?? "") || 0;
    return tb - ta;
  })[0]!;
}
